import React, { useEffect, useState, useRef} from 'react';
import {isBefore, isAfter, format, startOfMonth, addMonths,
  isSameDay, parse, differenceInMinutes, subDays, addDays,
  isWithinInterval, eachDayOfInterval, endOfWeek, startOfWeek, endOfMonth,
  addYears, startOfDay, endOfDay,
} from 'date-fns';
import { Button, Col, Row } from 'antd';
import { ReactComponent as DoublePrev } from '../../../../assets/images/svg/double_arrow_left.svg';
import { ReactComponent as DoubleNext } from '../../../../assets/images/svg/double_arrow_right.svg';

import { ReactComponent as SinglePrev } from '../../../../assets/images/svg/single_arrow_left.svg';
import { ReactComponent as SingleNext } from '../../../../assets/images/svg/single_arrow_right.svg';
import TimePickerDropdown from './TimePicker';
import './DatePicker.less';
import { centeredNotification,  DEFAULT_END_TIME_TPA } from '../../../../utils/utility';
import { useDispatch } from 'react-redux';
import { setDateApplied } from '../../../../store/actions/newAdmissionAction';
import { InfoCircleTwoTone } from '@ant-design/icons';
import { DATE_FORMAT_DD_MMM_YYYY, DATE_PICKER_CONSTANTS, DEFAULT_DOD_TIME, GUTTOR_SPACE } from '../../../constants/commonConstants';
import { getDateWithTextMonth } from '../../../../utils/dateTimeUtils';
import { DISCHARGE_TIME_MUST_BE_GREATER_THAN_30_MINS, DOD_VALIDATION_MESSAGE, TPA_RANGE_VALIDATION_MESSAGE } from '../../../constants/crmConstants';

const DatePicker = (props: any): any => {
  const {initialAdmitDate, initialDischargeDate, initialAdmitTime, initialDischargeTime, dodLimit, isSettlement,
    doaLeftLimit, doaRightLimit, setIsDatePickerOpen, setSelect, showNow, policyStartDate, policyEndDate, isDischargeCase,
    popConfirmCancel, delayedIntimationCancel, isEasyDischargeCase, disablefutureDates, isTpaDateSelector, disableDOD}
     = props;

  const dispatch = useDispatch();

  const [selectedAdmitDate, setSelectedAdmitDate] = useState(initialAdmitDate || undefined);
  const [selectedDischargeDate, setSelectedDischargeDate] = useState(initialDischargeDate || undefined);
  const [previousAdmitDate, setPreviousAdmitDate] = useState(initialAdmitDate || undefined);
  const [previousDischargeDate, setPreviousDischargeDate] = useState(initialDischargeDate || undefined);
  const getCurrentTime = (): string => format(new Date(), 'hh:mm aa');
  const [admitTime, setAdmitTime] = useState(initialAdmitTime || getCurrentTime());
  const [dischargeTime, setDischargeTime] = useState(initialDischargeTime ||
    (isTpaDateSelector ? DEFAULT_END_TIME_TPA() : DEFAULT_DOD_TIME));
  const [previousAdmitTime, setPreviousAdmitTime] = useState(initialAdmitTime || getCurrentTime());
  const [previousDischargeTime, setPreviousDischargeTime] = useState(initialDischargeTime || getCurrentTime());
  const [isPreventTimeSelection, setIsPreventTimeSelection] = useState(false);

  // Set currentMonth based on initialAdmitDate if it exists, otherwise use current date
  const [currentMonth, setCurrentMonth] =
    useState<Date>(initialAdmitDate ? startOfMonth(initialAdmitDate) : startOfMonth(new Date()));

  const [nextMon, setNextMonth] = useState<Date>(addMonths(currentMonth, 1));

  const daysOfWeek = [
    { day: 'M', fullName: 'Monday' },
    { day: 'T', fullName: 'Tuesday' },
    { day: 'W', fullName: 'Wednesday' },
    { day: 'T', fullName: 'Thursday' },
    { day: 'F', fullName: 'Friday' },
    { day: 'S', fullName: 'Saturday' },
    { day: 'S', fullName: 'Sunday' },
  ];
  const daysContainerRef = useRef<HTMLDivElement>(null);
  const nextDaysContainerRef = useRef<HTMLDivElement>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const now = new Date();

  useEffect((): void => {
    if ((selectedDischargeDate && popConfirmCancel) && !disableDOD) {
      setSelectedDischargeDate(undefined);
      setSelect(undefined, '', undefined, '');
    }
    if (selectedAdmitDate && delayedIntimationCancel) {
      setSelectedAdmitDate(undefined);
      setSelectedDischargeDate(undefined);
      setSelect('', '', '', '');
    }
  }, [popConfirmCancel, delayedIntimationCancel]);

  useEffect((): void => {
    setNextMonth(addMonths(currentMonth, 1));
  }, [currentMonth]);

  const isDischargeTimeValid = (admitDate: Date, dischargeDate: Date, admissionTime: string, dischargedTime: string): boolean => {
    if (isSameDay(admitDate, dischargeDate)) {
      const admitDateTime = parse(`${format(admitDate, 'yyyy-MM-dd')} ${admissionTime}`, 'yyyy-MM-dd hh:mm aa', new Date());
      const dischargeDateTime = parse(
        `${format(dischargeDate, 'yyyy-MM-dd')} ${dischargedTime}`, 'yyyy-MM-dd hh:mm aa', new Date());
        const DOA_DOD_diff_in_min = differenceInMinutes(dischargeDateTime, admitDateTime);
        return isTpaDateSelector
          ? DOA_DOD_diff_in_min >= 1
          : DOA_DOD_diff_in_min >= 30;
    } else if (isBefore(dischargeDate, admitDate)) {
      return false;
    }
    return true;
  };

  useEffect((): void => {
    if (admitTime && dischargeTime) {
      setValidationError(isDischargeTimeValid(selectedAdmitDate, selectedDischargeDate, admitTime, dischargeTime) ?
      null : isTpaDateSelector ? TPA_RANGE_VALIDATION_MESSAGE : DISCHARGE_TIME_MUST_BE_GREATER_THAN_30_MINS);
    }
    if (selectedAdmitDate) {
      const dodRightLimit = addDays(selectedAdmitDate, dodLimit);
      if (isAfter(new Date(), dodRightLimit)) {
        setIsPreventTimeSelection(true);
      } else {
        setIsPreventTimeSelection(false);
      }
    }
  }, [admitTime, dischargeTime, selectedAdmitDate, selectedDischargeDate]);

  const handleApply = (): void => {
    const validationMessage = !isDischargeTimeValid(selectedAdmitDate, selectedDischargeDate, admitTime, dischargeTime)
    ? (isTpaDateSelector ? TPA_RANGE_VALIDATION_MESSAGE : DOD_VALIDATION_MESSAGE)
    : null;
      if (validationMessage) {
        setValidationError(validationMessage);
        return;
      }
    setPreviousAdmitDate(selectedAdmitDate);
    setPreviousDischargeDate(selectedDischargeDate);
    setPreviousAdmitTime(admitTime);
    setPreviousDischargeTime(dischargeTime);
    dispatch(setDateApplied(true));
    setIsDatePickerOpen(false);
  };

  const handleCancel = (): void => {
    setSelectedAdmitDate(previousAdmitDate);
    setSelectedDischargeDate(previousDischargeDate);
    setAdmitTime(previousAdmitTime);
    setDischargeTime(previousDischargeTime);
    setSelect(
      previousAdmitDate ? format(previousAdmitDate, 'dd/MMM/yyyy') : '',
      previousDischargeDate ? format(previousDischargeDate, 'dd/MMM/yyyy') : '',
      previousAdmitDate && previousAdmitTime ? previousAdmitTime : '',
      previousDischargeDate && previousDischargeTime ? previousDischargeTime : '',
    );
    setIsDatePickerOpen(false);
  };

  const handleTimeSelected = (newTime: any): any => {
    setAdmitTime(newTime);
    setSelectedAdmitDate(selectedAdmitDate ? selectedAdmitDate : new Date());
    setSelect(
      selectedAdmitDate ? format(selectedAdmitDate, 'dd/MMM/yyyy') : format(new Date(), 'dd/MMM/yyyy'),
      undefined,
      newTime,
      undefined,
    );
  };

  const handleNextTimeSelected = (newTime: any): any => {
    setDischargeTime(newTime);
    setSelectedDischargeDate(selectedDischargeDate ? selectedDischargeDate : new Date());
    setSelect(
      undefined,
      selectedDischargeDate ? format(selectedDischargeDate, 'dd/MMM/yyyy') : checkDisabledDOD(selectedAdmitDate) ? format(selectedAdmitDate, 'dd/MMM/yyyy') : format(new Date(), 'dd/MMM/yyyy'),
      undefined,
      newTime,
    );
  };

  const handleMonthChange = (numMonths: number): any => {
    setCurrentMonth(addMonths(currentMonth, numMonths));
  };

  const handleNextMonthChange = (numMonths: number): any => {
    setNextMonth(addMonths(nextMon, numMonths));
  };

  const leftLimitDate = subDays(now, doaLeftLimit);
  const rightLimitDate = addDays(now, doaRightLimit);

  const checkDisabledDOA = (current: any): boolean => {
    return ((isDischargeCase || isSettlement || disablefutureDates) && isAfter(current, now))
      || isBefore(current, leftLimitDate)
      || isAfter(current, rightLimitDate)
      || (policyStartDate && isBefore(current, startOfDay(new Date(policyStartDate))))
      || (policyEndDate && isAfter(current, endOfDay(new Date(policyEndDate))));
  };

  const checkDisabledDOD = (current: Date): boolean => {
    if (!selectedAdmitDate) {
      return false; // No dates are disabled if admit date is not selected
    } else if (isBefore(current, selectedAdmitDate)) {
      return true;
    }

    const dodRightLimit = addDays(selectedAdmitDate, dodLimit);
    if (isAfter(current, dodRightLimit)) {
      return true;
    }
    if (isEasyDischargeCase) {
      return isBefore(current, now);
    }

   const sixMonthsAfterAdmit = addMonths(selectedAdmitDate, 6);
    return ((isDischargeCase || isSettlement || disablefutureDates)
    && isAfter(current, now)) || (isAfter(current, sixMonthsAfterAdmit) && !isTpaDateSelector);
  };

  const handleAdmitNowClick = (nowDate: any): any => {
    const admitNowTime = format(nowDate, 'hh:mm aa');
    setSelectedAdmitDate(nowDate);
    setAdmitTime(admitNowTime);
    setSelect(
      format(nowDate, 'dd/MMM/yyyy'),
      undefined,
      admitNowTime,
      undefined,
    );
    setSelectedDischargeDate(undefined);
    setCurrentMonth(startOfMonth(nowDate));
  };
  const handleDischargeNowClick = (nowDate: any): any => {
    const dischargeNowTime = format(nowDate, 'hh:mm aa');
    if (isBefore(nowDate, selectedAdmitDate)) {
      centeredNotification('Discharge Date cannot be less than Admit Date');
      return;
    }
    setSelectedDischargeDate(nowDate);
    setDischargeTime(dischargeNowTime);
    setSelect(
      undefined,
      format(nowDate, 'dd/MMM/yyyy'),
      undefined,
      dischargeNowTime,
    );
  };

  const handleYearChange = (numYears: number): any => {
    setCurrentMonth(addYears(currentMonth, numYears));
  };

  const handleNextYearChange = (numYears: number): any => {
    setNextMonth(addYears(nextMon, numYears));
  };

  const renderHeader = (): any => {
    const dateFormat = 'MMMM yyyy';
    return (
      <div className="datePickerHeader row flex-middle">
        <span className="column col-start">
          <span
            className="icon"
            onClick={(): any => {
              handleYearChange(-1);
              handleNextYearChange(-1);
            }
            }>
            {<DoublePrev />}
          </span>
          <span className="icon"
            onClick={(): any => {
              handleMonthChange(-1);
              handleNextMonthChange(-1);
              }
              }>
            {<SinglePrev />}
          </span>
        </span>
        <span className="column col-center">
          <span>{format(currentMonth, dateFormat)}</span>
        </span>
        <span className="column col-end">
          <span className="icon"
            onClick={(): any => {
              handleMonthChange(1);
              handleNextMonthChange(1);
              }
            }
          >{<SingleNext />}
          </span>
          <span
            className="icon"
            onClick={(): any => {
              handleYearChange(1);
              handleNextYearChange(1);
            }
            }>{<DoubleNext />}
          </span>
        </span>
      </div>
    );
  };

  const renderNextHeader = (): any => {
    const dateFormat = 'MMMM yyyy';
    return (
      <div className="datePickerHeader row flex-middle">
        <span className="column col-start">
          <span
            className="icon"
            onClick={(): any => {
              handleNextYearChange(-1);
              handleYearChange(-1);
            }
            }>
            {<DoublePrev />}
          </span>
          <span
            className="icon"
            onClick={(): any => {
              handleNextMonthChange(-1);
              handleMonthChange(-1);
              }
            }>{<SinglePrev />}
          </span>
        </span>
        <span className="column col-center">
          <span>{format(nextMon, dateFormat)}</span>
        </span>
        <span className="column col-end">
          <span className="icon"
            onClick={(): any => {
              handleNextMonthChange(1);
              handleMonthChange(1);
              }
            }
          >{<SingleNext />}
          </span>
          <span
            className="icon"
            onClick={(): any => {
              handleNextYearChange(1);
              handleYearChange(1);
            }
            }>{<DoubleNext />}
          </span>
        </span>
      </div>
    );
  };

  const renderDays = (): any => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // 1 represents Monday
    const endDate = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    return (
      <>
        <div className="days row">
          {daysOfWeek?.map((dayInfo: any): any => (
            <div className="col cell daysHeader" key={dayInfo?.fullName}>
              {dayInfo?.day}
            </div>
          ))}
        </div>
        <div className="days row" ref={daysContainerRef}>
          {days.map((day: any): any => (
            <div
              className={`col cell ${
                !sameMonth(day, currentMonth) ? 'disabled' :
                selectedAdmitDate && format(day, 'MM/dd/yyyy') === format(selectedAdmitDate, 'MM/dd/yyyy') ? 'selected' :
                selectedDischargeDate && format(day, 'MM/dd/yyyy') === format(selectedDischargeDate, 'MM/dd/yyyy') ? 'selected' :
                selectedAdmitDate && selectedDischargeDate && isAfter(selectedDischargeDate, selectedAdmitDate) &&
                isWithinInterval(day, {start: selectedAdmitDate, end: selectedDischargeDate}) ? 'dateRangeElements' :
                selectedAdmitDate && !selectedDischargeDate ? (checkDisabledDOD(day) ? 'disabledDates' : '') :
                checkDisabledDOA(day) ? 'disabledDates' :
                format(new Date(), 'MM/dd/yyyy') === format(day, 'MM/dd/yyyy') ? 'currentDate' : ''}
                ${selectedAdmitDate && selectedDischargeDate && checkDisabledDOA(day) ? 'disabledRangeElements' : ''}`}
              key={day.toString()}
              onClick={(): any => handleDayClick(day)}>
              {format(day, 'd')}
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderNextDays = (): any => {
    const monthStart = startOfMonth(nextMon);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // 1 represents Monday
    const endDate = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    return (
      <>
        <div className="days row">
          {daysOfWeek?.map((dayInfo: any): any => (
            <div className="col cell daysHeader" key={dayInfo?.fullName}>
              {dayInfo?.day}
            </div>
          ))}
        </div>
        <div className="days row" ref={nextDaysContainerRef}>
          {days.map((day: any): any => (
            <div
              className={`col cell ${
                !sameMonth(day, nextMon) ? 'disabled' :
                selectedAdmitDate && format(day, 'MM/dd/yyyy') === format(selectedAdmitDate, 'MM/dd/yyyy') ? 'selected' :
                selectedDischargeDate && format(day, 'MM/dd/yyyy') === format(selectedDischargeDate, 'MM/dd/yyyy') ? 'selected' :
                selectedAdmitDate && selectedDischargeDate && isAfter(selectedDischargeDate, selectedAdmitDate) &&
                isWithinInterval(day, {start: selectedAdmitDate, end: selectedDischargeDate}) ? 'dateRangeElements' :
                selectedAdmitDate && !selectedDischargeDate ? (checkDisabledDOD(day) ? 'disabledDates' : '') :
                checkDisabledDOA(day) ? 'disabledDates' :
                format(new Date(), 'MM/dd/yyyy') === format(day, 'MM/dd/yyyy') ? 'currentDate' : ''}
                ${selectedAdmitDate && selectedDischargeDate && checkDisabledDOA(day) ? 'disabledRangeElements' : ''}`}
              key={day.toString()}
              onClick={(): any => handleDayClick(day)}>
              {format(day, 'd')}
            </div>
          ))}
        </div>
      </>
    );
  };

  const handleDayClick = (day: Date): void => {
    if ((!selectedAdmitDate || (selectedAdmitDate && selectedDischargeDate)) && checkDisabledDOA(day)) {
      return;
    }

    if (disableDOD) { // if dod is not allowed to select then the click to be considered as DOA
      setSelectedAdmitDate(day);
      setSelect(format(day, DATE_FORMAT_DD_MMM_YYYY), format(selectedDischargeDate, DATE_FORMAT_DD_MMM_YYYY), admitTime, dischargeTime);
    } else if (!selectedAdmitDate || (selectedAdmitDate && selectedDischargeDate)) {
      setSelectedAdmitDate(day);
      if (!isDischargeCase || (selectedAdmitDate && selectedDischargeDate)) {
        setSelectedDischargeDate(undefined);
        setSelect(format(day, DATE_FORMAT_DD_MMM_YYYY), '', admitTime, '');
      } else {
        setSelect(format(day, DATE_FORMAT_DD_MMM_YYYY), format(selectedDischargeDate, DATE_FORMAT_DD_MMM_YYYY), admitTime, dischargeTime);
      }
    } else if (selectedAdmitDate && !selectedDischargeDate) {
      if (!checkDisabledDOD(day)) {
        setSelectedDischargeDate(day);
        setSelect(format(selectedAdmitDate, DATE_FORMAT_DD_MMM_YYYY), format(day, DATE_FORMAT_DD_MMM_YYYY), admitTime, dischargeTime);
      }
    }
  };

  const sameMonth = (day: Date, month: Date): any => {
    return format(day, 'MM/yyyy') === format(month, 'MM/yyyy');
  };

  return <Row gutter={24}  className="datePickerComponent">
    <Col md={12} className="datePickerCalender">
      {renderHeader()}
      <TimePickerDropdown
        onTimeSelect={handleTimeSelected}
        isAdmitDateSelected={selectedAdmitDate}
        onSelectNow={handleAdmitNowClick}
        showNow={showNow}
        initialTime={admitTime} />
      {renderDays()}

    </Col>
    <Col md={12} className="datePickerCalender">
      {renderNextHeader()}
      <TimePickerDropdown
        onTimeSelect={handleNextTimeSelected}
        isAdmitDateSelected={selectedAdmitDate}
        onSelectNow={handleDischargeNowClick}
        showNow={showNow}
        isPreventTimeSelection={isPreventTimeSelection}
        initialTime={dischargeTime} />
      {renderNextDays()}
    </Col>
  {
        policyEndDate &&
        <Row gutter={GUTTOR_SPACE} className="mb-md footerTextDiv">
          <Col md={24} className="footerText">
              <InfoCircleTwoTone className="mr-5" />
              Policy expiry date is {getDateWithTextMonth(policyEndDate)}.
          </Col>
        </Row>
      }
      <hr/>

    {validationError && (
        <div className="validationError" style={{ color: 'red', marginBottom: '10px' }}>
          {validationError}
        </div>
      )}
    <div className="datePickerFooter">
      {!selectedAdmitDate && <span className="datePicker-footer-text">{DATE_PICKER_CONSTANTS.SELECT_ADMISSION_DATE}</span>}
      {selectedAdmitDate && !selectedDischargeDate && !isTpaDateSelector &&
       <span className="datePicker-footer-text">{DATE_PICKER_CONSTANTS.SELECT_DISCHARGE_DATE}</span>}
      {selectedAdmitDate && selectedDischargeDate && !isTpaDateSelector &&
      <span className="datePicker-footer-text">{DATE_PICKER_CONSTANTS.APPLY_THE_DATES}</span>}
       {selectedAdmitDate && !selectedDischargeDate && isTpaDateSelector &&
        <span className="datePicker-footer-text">{DATE_PICKER_CONSTANTS.SELECT_RANGE_END_DATE}</span>}
      {selectedAdmitDate && selectedDischargeDate && isTpaDateSelector &&
      <span className="datePicker-footer-text">{DATE_PICKER_CONSTANTS.SELECT_RANGE_START_DATE}</span>}
      <span className="datePicker-footer-btn">
        <Button size="small" className="cancel-btn" onClick={handleCancel}>Cancel</Button>
        <Button
          size="small"
          className="datepicker-apply"
          type="primary"
          onClick={handleApply}
          disabled={!selectedAdmitDate || !selectedDischargeDate}
        >
          Apply
        </Button>
      </span>
    </div>
  </Row>;
};

export default DatePicker;
